/** Desk window onto the physical keyboard, in metres; room for both hands around a full-size keyboard. */
export const XR_KEYBOARD_WINDOW = {
    width: 0.6, depth: 0.32, soft: 0.05,
    /** Fraction of the scene's background colour kept over the camera image, so the room is tinted and dimmed to match. */
    dim: 0.3,
    /** Distance along the gaze to a keyboard the viewer is looking down at. */
    gazeDistance: 0.55,
    /** Gaze must point at least this far below horizontal (sine of the pitch) to be aimed at a desk. */
    minDownward: 0.35,
    /** Fallback when the viewer is not looking down: ahead of and below the eyes of a seated typist. */
    fallbackAhead: 0.4, fallbackBelow: 0.5,
};
/**
 * The window follows the body, estimated lazily from the head: head turns and leans within the dead bands are
 * glances, while staying beyond them for `dwellSeconds` (turning the chair, shifting seat) recentres the body.
 */
export const XR_KEYBOARD_FOLLOW = { yawDeadBand: 0.8, shiftDeadBand: 0.35, dwellSeconds: 1, recentreSeconds: 0.35 };
/** Head heading about the vertical; stays defined when looking straight down because the crown points forward. */
export function resolveXrHeadYaw(rotation) {
    const { x, y, z, w } = rotation;
    const forwardX = -2 * (x * z + w * y) + 2 * (x * y - w * z);
    const forwardZ = -(1 - 2 * (x * x + y * y)) + 2 * (y * z + w * x);
    return Math.hypot(forwardX, forwardZ) < 1e-6 ? 0 : Math.atan2(-forwardX, -forwardZ);
}
/** Places the horizontal keyboard window where the head looks, in the same (rig) space as the head pose. */
export function resolveXrKeyboardWindow(head, rotation) {
    const { x, y, z, w } = rotation;
    // Head forward (0, 0, -1) rotated by the quaternion.
    const gaze = { x: -2 * (x * z + w * y), y: -2 * (y * z - w * x), z: -(1 - 2 * (x * x + y * y)) };
    const yaw = resolveXrHeadYaw(rotation);
    if (gaze.y <= -XR_KEYBOARD_WINDOW.minDownward) {
        const d = XR_KEYBOARD_WINDOW.gazeDistance;
        return { position: [head.x + gaze.x * d, head.y + gaze.y * d, head.z + gaze.z * d], yaw, aimed: true };
    }
    const ahead = XR_KEYBOARD_WINDOW.fallbackAhead;
    return { position: [head.x - Math.sin(yaw) * ahead, head.y - XR_KEYBOARD_WINDOW.fallbackBelow,
            head.z - Math.cos(yaw) * ahead], yaw, aimed: false };
}
const wrap = (angle) => Math.atan2(Math.sin(angle), Math.cos(angle));
/** Ties a placed window to the body at the head's current pose. */
export function anchorXrKeyboardWindow(window, head, headYaw) {
    const dx = window.position[0] - head.x;
    const dz = window.position[2] - head.z;
    const cos = Math.cos(headYaw), sin = Math.sin(headYaw);
    return {
        body: { x: head.x, z: head.z, yaw: headYaw, dwell: 0, recentring: false },
        right: dx * cos - dz * sin, ahead: -(dx * sin + dz * cos), y: window.position[1], yaw: wrap(window.yaw - headYaw),
    };
}
/** Advances the lazily followed body toward the head. */
export function followXrBody(body, head, headYaw, delta) {
    const dt = Math.max(0, delta);
    const turn = wrap(headYaw - body.yaw);
    const shift = Math.hypot(head.x - body.x, head.z - body.z);
    const outside = Math.abs(turn) > XR_KEYBOARD_FOLLOW.yawDeadBand || shift > XR_KEYBOARD_FOLLOW.shiftDeadBand;
    const dwell = outside ? body.dwell + dt : 0;
    let recentring = body.recentring || dwell >= XR_KEYBOARD_FOLLOW.dwellSeconds;
    if (!recentring)
        return { ...body, dwell };
    const k = 1 - Math.exp(-dt / XR_KEYBOARD_FOLLOW.recentreSeconds);
    const next = { x: body.x + (head.x - body.x) * k, z: body.z + (head.z - body.z) * k, yaw: wrap(body.yaw + turn * k) };
    if (Math.abs(wrap(headYaw - next.yaw)) < 0.03 && Math.hypot(head.x - next.x, head.z - next.z) < 0.02)
        recentring = false;
    return { ...next, dwell: 0, recentring };
}
/** The window pose for the anchor's current body. */
export function resolveXrAnchoredWindow(anchor) {
    const { body } = anchor;
    const cos = Math.cos(body.yaw), sin = Math.sin(body.yaw);
    return {
        position: [body.x + anchor.right * cos - anchor.ahead * sin, anchor.y, body.z - anchor.right * sin - anchor.ahead * cos],
        yaw: body.yaw + anchor.yaw,
    };
}
